/**
 * CRM shared types
 *
 * Mirrors the backend response shapes from routes/crm.js. `screener` is the
 * label-resolved view built server-side (crmScreener.js); `answers` is the raw
 * typed lead_answers (kept for back-compat).
 */

/** A recruiter-composed SMS/email queued for a future send (Schedule-send).
 *  Mirrors crm.scheduled_messages; fired by the crm-worker tick. */
export interface ScheduledMessage {
  id: string;
  network_id: string;
  contact_id: string;
  channel: 'sms' | 'email';
  subject: string | null;
  body: string;
  study_id: string | null;
  study_document_ids: string[];
  send_at: string;
  status: 'scheduled' | 'sending' | 'sent' | 'failed' | 'canceled';
  attempts: number;
  sent_at: string | null;
  communication_id: string | null;
  /** Breadcrumb: block reason / send error / 'deferred:<iso>' (quiet hours). */
  last_error: string | null;
  created_by: string | null;
  canceled_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface Contact {
  id: string;
  network_id?: string;
  site_id?: string | null;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  phone_e164: string | null;
  email: string | null;
  lifecycle_status: string | null;
  best_eligibility: string | null;
  /** Best landing-form eligibility verdict across this lead's studies,
   *  denormalized for filtering/sorting (eligible|needs_review|pending|
   *  not_eligible). Same field the inbox eligibility filter uses. */
  incoming_eligibility?: string | null;
  owner_user_id?: string | null;
  /** The one study this patient is being considered for (the chip under their
   *  name + the Call Mode default). Auto-set when a prescreen starts; editable. */
  primary_study_id?: string | null;
  tags?: string[];
  sms_opt_out: boolean;
  /** Conversation state (denormalized from communications by a DB trigger). */
  last_inbound_at?: string | null;
  last_outbound_at?: string | null;
  last_activity_at?: string | null;
  /** Recruiter archived this conversation (an inbound reply auto-reopens it). */
  archived_at?: string | null;
  /** Worklist state (denormalized from communications by a DB trigger). */
  needs_reply_at?: string | null;
  /** Outbound attempts since the lead last engaged (resets on any inbound). */
  attempt_count?: number;
  /** When the next outreach attempt is due (cadence engine); null = paused. */
  next_action_at?: string | null;
  stage_changed_at?: string | null;
  /** Recruiter snoozed this lead off the deferrable worklist buckets until this
   *  moment (#6). NULL = active; a future value hides it from new_leads/
   *  follow_ups/stale (auto-resurfaces when it passes). Never hides needs_reply. */
  snoozed_until?: string | null;
  created_at: string;
  updated_at?: string;
}

/** Conversation-state filter for GET /contacts (?conversation=). */
export type ConversationFilter = 'active' | 'awaiting' | 'needs_reply' | 'archived';

/** Per-contact study link summary returned on the inbox list. */
export interface StudyLink {
  study_slug: string | null;
  /** Public study name frozen on the lead snapshot at intake. */
  study_title: string | null;
  /** Recruiter-only internal alias overlaid live (null = none); render
   *  `study_internal_title || study_title` in recruiter views. */
  study_internal_title?: string | null;
  lead_source: string | null;
  eligibility_status: string | null;
  first_seen_at: string | null;
}

/** Most recent communication, embedded on the inbox list for triage. */
export interface LastComm {
  channel: string;
  direction: string | null;
  status: string | null;
  body: string | null;
  occurred_at: string;
}

/**
 * The single most pertinent "where is this lead now" signal, derived server-side
 * (crmMilestone.deriveMilestone) and rendered under the lifecycle badge in the
 * inbox / conversations / board lists. `key` is the semantic milestone (the UI
 * owns its label, icon, and tone); `at` carries the callback time for the
 * call_scheduled / call_overdue keys (null otherwise).
 */
export type MilestoneKey =
  | 'needs_reply'
  | 'call_scheduled'
  | 'call_overdue'
  | 'appt_scheduled'
  | 'needs_first_contact'
  | 'follow_up_due'
  | 'awaiting_records'
  | 'ready_to_schedule'
  | 'going_stale';

export interface Milestone {
  key: MilestoneKey;
  at: string | null;
}

/** Row shape of GET /api/crm/contacts (enriched inbox payload). */
export interface ContactListItem extends Contact {
  studies: StudyLink[];
  last_comm: LastComm | null;
  /** Derived pertinent milestone (null when none applies — see Milestone). */
  milestone?: Milestone | null;
}

/** GET /api/crm/team — assignable recruiters/staff for the CRM network. */
export interface TeamMember {
  id: string;
  name: string;
  email: string | null;
  role?: string | null;
}

/**
 * GET /api/crm/studies row — CRM studies merged with lead counts by slug.
 * source 'crm' = a crm.studies row (id set); source 'leads' = seen in lead
 * data only (id null) — the hub offers adoption for those.
 */
export interface StudyOption {
  slug: string;
  title: string | null;
  /** Admin-only internal title (recruiter views); falls back to title. Null for
   *  lead-only stubs. */
  internal_title?: string | null;
  count: number;
  id?: string | null;
  /** draft | recruiting | paused | closed (null for lead-only stubs). */
  status?: string | null;
  /** When an admin archived the study (hidden from the default list). */
  archived_at?: string | null;
  source?: 'crm' | 'leads';
  sponsor_name?: string | null;
  protocol_number?: string | null;
  /** Registry link (public.protocols id) — null = unlinked legacy study. */
  protocol_id?: string | null;
  therapeutic_area?: string | null;
  stipend_summary?: StipendSummary | null;
  /** Active published prescreen version (null = none yet). */
  prescreen_version?: number | null;
  /** Live on the public WCR website (the "Show on website" toggle). */
  show_on_website?: boolean;
  is_featured?: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * GET /api/crm/study-lead-stats?study=<slug> — exact per-lifecycle lead counts
 * for one study (the by-study view). `by_status` has every canonical lifecycle
 * key (zeros kept); `considering` is the active-funnel roll-up.
 */
export interface StudyLeadStats {
  study: string;
  by_status: Record<string, number>;
  total: number;
  considering: number;
}

/**
 * Result of POST /api/crm/studies/import-leads — the per-upload tally.
 * created + merged + flagged + duplicate_in_file + invalid === total.
 */
export interface LeadImportSummary {
  total: number;
  /** New contacts created. */
  created: number;
  /** Linked to an existing patient (merged by phone/email). */
  merged: number;
  /** Matched but conflicting (phone vs email point at different people) — review. */
  flagged: number;
  /** Same phone appeared earlier in the file. */
  duplicate_in_file: number;
  /** Rows that couldn't be imported (bad/missing phone, etc.). */
  invalid: number;
  errors: { row: number; error: string }[];
}

// ---------------------------------------------------------------------------
// Study Hub (Expansion Phase A)
// ---------------------------------------------------------------------------

/** Live stipend/compensation summary on a study (human-edited; AI-publishable). */
export interface StipendSummary {
  amount_text?: string | null;
  schedule_text?: string | null;
  notes?: string | null;
}

export interface Study {
  id: string;
  slug: string;
  /** Public-facing study name: drives the slug, the WCR public website, and the
   *  {{study_title}} SMS/email merge. Patient-visible. */
  title: string;
  /** Admin-only internal/working title shown in recruiter CRM views; falls back
   *  to `title` when null. NEVER public (no slug, website, or merge use). */
  internal_title: string | null;
  sponsor_name: string | null;
  protocol_number: string | null;
  /** Registry link (public.protocols id) — the eReg source-of-truth record.
   *  Required on new studies while registry reads are on; null = unlinked
   *  legacy study (shows the "Link to eReg" banner on StudyDetail). */
  protocol_id?: string | null;
  therapeutic_area: string | null;
  /** draft | recruiting | paused | closed. */
  status: string;
  stipend_summary: StipendSummary;
  /** Lead count by slug (embedded on the detail response). */
  count?: number;
  // Public website ("Show on website") — marketing/SEO copy + the publish gate.
  // Drive the WCR public studies listing + each study's information page.
  show_on_website?: boolean;
  is_featured?: boolean;
  condition?: string | null;
  compensation_text?: string | null;
  short_description?: string | null;
  full_description?: string | null;
  requirements_summary?: string[] | null;
  age_range?: string | null;
  study_duration?: string | null;
  visit_count?: string | null;
  meta_title?: string | null;
  meta_description?: string | null;
  /** The one published form the ad landing page (/study/<slug>) serves. */
  landing_form_id?: string | null;
  /** Item #05: the one published questionnaire texted to leads (respond-options). */
  questionnaire_form_id?: string | null;
  /**
   * Per-study JSONB config blob (study detail returns the full row). Holds the
   * setup-checklist draft and the item #01 ineligible-CTA toggle, among others.
   */
  config?: {
    ineligible_cta?: { enabled?: boolean } | null;
    /** Per-study opt-out of the generic network-wide welcome flow (Flows tab). */
    suppress_generic_welcome?: boolean;
    /** CTMS (CRIO) mapping: links this study to a CRIO study id (+ optional site override). */
    crio?: { study_id?: string | null; site_id?: string | null } | null;
    [key: string]: unknown;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface StudyDocument {
  id: string;
  /** protocol | icf | amendment | other. */
  doc_type: string;
  filename: string;
  mime_type: string | null;
  file_size: number | null;
  page_count: number | null;
  version_label: string | null;
  /** pending | processing | complete | failed. */
  extraction_status: string;
  extraction_error: string | null;
  created_at: string;
  updated_at?: string;
  /** 'registry' = a CURRENT eReg ICF served through the CRM proxy for linked
   *  studies (composer attach picker); absent = legacy crm.study_documents row. */
  source?: 'registry';
}

export type ArtifactType = 'summary' | 'recruiter_script' | 'prescreen_draft' | 'stipend_info' | 'website_content' | 'message_templates';

/** A reusable composer reply template (crm.message_templates). study_id null = global. */
export interface MessageTemplate {
  id: string;
  study_id: string | null;
  /** sms | email | any. */
  channel: 'sms' | 'email' | 'any';
  title: string;
  subject: string | null;
  body: string;
  /** Picker tab bucket: welcome | study_details | general. */
  category: 'welcome' | 'study_details' | 'general';
  /** manual | ai. */
  source: 'manual' | 'ai';
  /** active | archived. */
  status: 'active' | 'archived';
  sort_order: number;
  created_by: string | null;
  created_at: string;
  updated_at?: string;
}

/** AI-generated, human-edited recruiter asset. content shape varies by type. */
export interface StudyArtifact {
  id: string;
  artifact_type: ArtifactType;
  version: number;
  /** generating | draft | published | superseded | failed. */
  status: string;
  content: any;
  source_document_id: string | null;
  /** eReg version ids this artifact was generated from (registry reads, §3.5);
   *  null = legacy crm-document source. Drives "source updated" badges. */
  source_version_ids?: string[] | null;
  model_id?: string | null;
  /**
   * Persisted generation knobs. `revised_from` marks a hand-edited copy of an
   * earlier version; `instructions` is the admin's free-text direction for an AI
   * build; `ai_revise` is present only while a draft is being AI-revised (a
   * generating prescreen draft).
   */
  generation_options?: {
    revised_from?: number;
    length?: string;
    instructions?: string;
    ai_revise?: { instructions: string };
  } | null;
  error: string | null;
  generated_at: string | null;
  published_at: string | null;
  created_at: string;
  updated_at?: string;
}

/**
 * A calculated (derived) criterion's spec — its value is computed from OTHER
 * answers, not asked directly. Published inputs map a slot to a frozen
 * question_key; draft inputs map a slot to a sibling criterion's `ref`.
 */
export interface PrescreenCalculation {
  /** age | bmi (see formMeta.CALC_PRESETS). */
  preset: string;
  inputs: Record<string, string>;
}

/** One prescreen question/criterion in a published version. */
export interface PrescreenCriterion {
  id: string;
  /** inclusion | exclusion. */
  criterion_kind: string;
  category: string;
  /** Exact protocol wording (provenance). */
  source_text: string;
  /** Layman's-terms question the recruiter asks. */
  display_question: string;
  question_key: string;
  profile_key: string | null;
  /** boolean | number | choice | text | date | height (ft+in, stored as total inches). */
  answer_type: string;
  options: { value: string; label: string }[] | null;
  /** Eligibility check: a leaf {op,…} or an AND/OR group {combinator,conditions}. */
  rule: any;
  /** Present only on derived criteria; inputs hold frozen question_keys. */
  calculation?: PrescreenCalculation | null;
  sort_order: number;
}

/** Draft criterion inside a prescreen_draft artifact (pre-publish, no keys yet). */
export interface DraftCriterion {
  criterion_kind: string;
  category: string;
  source_text: string;
  display_question: string;
  answer_type: string;
  options: { value: string; label: string }[] | null;
  rule: any;
  /** Present only on derived criteria; inputs hold sibling `ref`s. */
  calculation?: PrescreenCalculation | null;
  /** Stable draft-local id wiring calculation inputs across reorders. */
  ref?: string;
  profile_key_suggestion: string | null;
}

/** The study's active published prescreen. */
export interface Prescreen {
  id: string;
  version: number;
  status: string;
  published_at: string;
  /** The prescreen_draft artifact this version was published from (fork source for a hand-edit). */
  source_artifact_id?: string | null;
  criteria: PrescreenCriterion[];
}

/** The linked registry record embedded on the study detail (plan §5.1). */
export interface StudyRegistryInfo {
  protocol_id: string;
  protocol_number: string | null;
  study_nickname: string | null;
  sponsor_name: string | null;
  site: { id: string; name: string } | null;
  /** Registry-level soft-archive stamp — surfaces the §6-E7 "archived in eReg" chip. */
  archived_at: string | null;
}

/** §3.5 staleness roll-up: derived rows whose eReg source versions are superseded. */
export interface StudyStaleness {
  any: boolean;
  artifacts: { id: string; artifact_type: ArtifactType; status: string }[];
  criteria_sets: { id: string; status: string }[];
  prescreen: boolean;
}

/** GET /api/crm/studies/:id — full hub detail. */
export interface StudyDetailResponse {
  study: Study;
  documents: StudyDocument[];
  artifacts: StudyArtifact[];
  prescreen: Prescreen | null;
  /** The §7 rollback flag — false restores the pre-P2 legacy behavior. */
  registry_reads_enabled?: boolean;
  /** Linked registry record (null = unlinked, foreign, or flag off). */
  registry?: StudyRegistryInfo | null;
  /** Null when unlinked / flag off / lookup degraded. */
  staleness?: StudyStaleness | null;
}

// ---------------------------------------------------------------------------
// Study setup checklist (Study Hub onboarding)
// ---------------------------------------------------------------------------

/** How the UI completes a checklist item. */
export interface SetupChecklistAction {
  /** tab = switch study-detail tab; route = navigate; set_status = one-click PATCH; none = manual ack. */
  type: 'tab' | 'route' | 'set_status' | 'none';
  value?: string;
}

/** One computed checklist item (catalog meta + live status). */
export interface SetupChecklistItem {
  key: string;
  group: string;
  label: string;
  description: string;
  required: boolean;
  action: SetupChecklistAction;
  /** True when the item has a data signal (vs manual-only). */
  autoDetected: boolean;
  status: 'done' | 'in_progress' | 'todo';
  /** 'auto' = detected from data, 'manual' = admin ticked it, null = not done. */
  source: 'auto' | 'manual' | null;
  /** A human explicitly signed off on this done item (stronger than the auto/AI check). */
  verified: boolean;
  /** ISO timestamp of the sign-off, when verified. */
  verifiedAt: string | null;
  /** User id who signed off, when verified (captured for audit). */
  verifiedBy: string | null;
}

/** A stored human-verification sign-off record. */
export interface SetupChecklistVerification {
  at: string;
  by: string | null;
}

/** Persisted draft state living in studies.config.setup_checklist. */
export interface SetupChecklistDraft {
  checks: Record<string, boolean>;
  verified: Record<string, SetupChecklistVerification>;
  completed_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

/** GET /api/crm/studies/:id/setup. */
export interface SetupChecklistResponse {
  study: { id: string; slug: string; title: string; status: string };
  groups: { key: string; label: string }[];
  items: SetupChecklistItem[];
  progress: {
    done: number; total: number;
    requiredDone: number; requiredTotal: number;
    verified: number; requiredVerified: number;
  };
  draft: SetupChecklistDraft;
}

// ---------------------------------------------------------------------------
// Scheduling gates — a study's required prescreening milestones + the per-lead
// readiness checklist that warns a recruiter before they schedule too early.
// ---------------------------------------------------------------------------

/** A standard milestone in the catalog (auto = derived from data, not a manual tick). */
export interface ScheduleGateCatalogItem {
  key: string;
  label: string;
  description: string;
  auto: boolean;
}

/** A configured (selected) requirement gate for a study — every one is required. */
export interface ScheduleRequirementGate {
  key: string;
  label: string;
  description: string;
  auto: boolean;
  custom: boolean;
  required: boolean;
}

/** GET/PUT /api/crm/studies/:id/schedule-requirements. */
export interface StudyScheduleRequirements {
  studyId: string;
  gates: ScheduleRequirementGate[];
  raw: { gates: string[]; custom: { key: string; label: string }[] };
  catalog: ScheduleGateCatalogItem[];
  updatedAt: string | null;
  updatedBy: string | null;
}

/** One computed readiness item for a lead (auto-derived or a manual sign-off). */
export interface ScheduleReadinessItem {
  key: string;
  label: string;
  description: string;
  auto: boolean;
  custom: boolean;
  required: boolean;
  done: boolean;
  source: 'auto' | 'manual' | null;
  note: string | null;
  doneAt: string | null;
  doneBy: string | null;
}

/** GET /api/crm/contacts/:id/schedule-gates. */
export interface ScheduleReadiness {
  configured: boolean;
  study_id: string | null;
  items: ScheduleReadinessItem[];
  progress: { done: number; total: number };
  unmet: { key: string; label: string }[];
  allMet: boolean;
}

// ---------------------------------------------------------------------------
// Study attempts + disqualifications (Expansion Phase B)
// ---------------------------------------------------------------------------

/** Saved prescreen answer on an attempt (typed columns, lead_answers shape). */
export interface AttemptAnswer {
  attempt_id?: string;
  question_key: string;
  question_type: string | null;
  value_text: string | null;
  value_num: number | null;
  value_bool: boolean | null;
  value_json: unknown;
  updated_at?: string;
}

/** Structured disqualification (why a lead screened out). */
export interface Disqualification {
  id: string;
  attempt_id?: string | null;
  criterion_id: string | null;
  category: string;
  note: string | null;
  recruiter_user_id: string | null;
  lead_source?: string | null;
  occurred_at: string;
  voided_at?: string | null;
  /** Embedded on the analytics drill-down list. */
  contact?: Pick<Contact, 'id' | 'full_name' | 'first_name' | 'last_name'> | null;
  study_title?: string | null;
  criterion?: Pick<PrescreenCriterion, 'id' | 'display_question' | 'criterion_kind'> | null;
}

/**
 * One resolved landing-form question + the patient's answer (label-resolved by
 * crmScreener.displayAnswer). Shared by the profile screener modal and the
 * Reports DQ drill-down. `kind` mirrors eligibility_reasons for highlighting.
 */
export interface LandingAnswer {
  question: string;
  answer: string;
  /** 'exclude' (disqualifying, red) | 'review' (flagged, yellow) | null. */
  kind?: 'exclude' | 'review' | null;
}

/** One contact-x-study screening journey (GET /contacts/:id/attempts). */
export interface StudyAttempt {
  id: string;
  study_id: string;
  study_title?: string | null;
  study_slug?: string | null;
  prescreen_version_id: string | null;
  /** lead | recruiter | pivot | form. */
  source: string;
  /** screening | eligible | disqualified | withdrew | scheduled | enrolled | completed | discontinued. */
  status: string;
  started_by: string | null;
  started_at: string;
  outcome_at: string | null;
  answers: AttemptAnswer[];
  disqualification: Disqualification | null;
  /** Study-matched landing-form Q&A attached by getAttemptDetail (item #2). */
  landing_answers?: LandingAnswer[];
}

// ---------------------------------------------------------------------------
// Cross-study pivot (Expansion Phase F)
// ---------------------------------------------------------------------------

/** The fact that decided a criterion's verdict (null = nothing known). */
export interface StudyMatchFact {
  value: any;
  /** prescreen | form | lead | profile (Phase G meds/conditions). */
  source: string;
  answered_at: string | null;
}

/** One evaluated criterion inside a study match (sort_order order). */
export interface StudyMatchCriterion {
  id: string;
  display_question: string;
  /** inclusion | exclusion. */
  criterion_kind: string;
  category: string;
  sort_order: number;
  verdict: 'pass' | 'fail' | 'unknown';
  fact: StudyMatchFact | null;
}

/** GET /api/crm/contacts/:id/study-matches — one ranked candidate study. */
export interface StudyMatch {
  study: { id: string; slug: string; title: string; internal_title?: string | null; therapeutic_area: string | null };
  /** True when the contact already has an OPEN screening attempt here. */
  resume: boolean;
  open_attempt_id: string | null;
  passes: number;
  fails: number;
  unknowns: number;
  criteria: StudyMatchCriterion[];
}

/** GET /api/crm/analytics/* aggregate row. */
export interface AnalyticsRow {
  group_key: string;
  group_label: string;
  total: number;
}

/** GET /api/crm/analytics/funnel row (lifecycle per group). */
export interface FunnelRow extends AnalyticsRow {
  lifecycle_status: string;
}

/**
 * Live call state for a recruiter, surfaced by the voice presence store and
 * the Calls dashboard. Present only while the recruiter is on a call right now.
 */
export interface LiveCallState {
  on_call: true;
  direction: 'outbound' | 'inbound' | null;
  contact_first_name: string | null;
  /** ISO timestamp the call connected; the UI ticks duration from here to now. Null on the degraded presence path. */
  started_at: string | null;
}

/** Per-recruiter range-window call statistics (GET /api/crm/analytics/calls). */
export interface RecruiterCallStats {
  total: number;
  outbound: number;
  inbound: number;
  completed: number;
  missed: number;
  talk_time_sec: number;
  avg_sec: number;
  last_call_at: string | null;
}

/** One recruiter row in the Calls dashboard (live state + range stats). */
export interface RecruiterCalls {
  user_id: string;
  name: string;
  live: LiveCallState | null;
  stats: RecruiterCallStats;
}

/** GET /api/crm/analytics/calls — the per-recruiter Calls dashboard payload. */
export interface CallsAnalytics {
  range: { from: string | null; to: string | null };
  recruiters: RecruiterCalls[];
  totals: {
    total: number;
    outbound: number;
    inbound: number;
    completed: number;
    missed: number;
    talk_time_sec: number;
    on_call_now: number;
    recruiters_online: number;
  };
  unattributed: { total: number; talk_time_sec: number };
  /** True when a study filter was applied (live presence stays network-wide). */
  study_scoped?: boolean;
}

/** The four real patient-comm channels surfaced on the live feed. */
export type CommFeedChannel = 'sms' | 'email' | 'call' | 'fax';

/**
 * One row on the live communications board (Reports → Live feed, admin-only).
 * A unified shape across SMS / email / voice call / fax: the patient (name +
 * pipeline status), who handled it, and what was said (`preview`, plus `subject`
 * for email and `duration_sec`/`voicemail` for calls).
 */
export interface CommFeedRow {
  id: string;
  channel: CommFeedChannel;
  direction: 'inbound' | 'outbound' | null;
  status: string | null;
  occurred_at: string;
  /** Staff member who sent/handled it; null for inbound or automated sends. */
  user_id: string | null;
  user_name: string | null;
  contact_id: string | null;
  contact_name: string;
  contact_status: string | null;
  /** Email subject line (email only). */
  subject: string | null;
  /** What was said: SMS/email body, call transcript-or-disposition, fax summary. */
  preview: string;
  duration_sec: number | null;
  voicemail: boolean;
  to_number: string | null;
  from_number: string | null;
  page_count: number | null;
  error: string | null;
}

/** GET /api/crm/analytics/communications — the live communications feed payload. */
export interface CommFeedResponse {
  rows: CommFeedRow[];
  /** Network staff roster for the "filter by user" dropdown. */
  team: { id: string; name: string }[];
  has_more: boolean;
  /** Cursor for the next (older) page — pass back as ?before. */
  next_before: string | null;
  study_scoped?: boolean;
}

/**
 * GET /api/crm/analytics/stage-conversion row — one per group x forward-spine
 * stage. A cohort "reached-stage" funnel (high-water mark) with stage-to-stage
 * conversion % and time-in-stage, NOT the point-in-time occupancy funnel.
 */
export interface StageConversionRow extends AnalyticsRow {
  stage: string;
  ordinal: number;
  /** Cohort contacts whose pipeline high-water reached at least this stage. */
  reached: number;
  /** % that converted from the PREVIOUS stage into this one (null at the base). */
  conversion_pct: number | null;
  /** Median / p90 total time-in-stage (completed stints only), in seconds. */
  median_dwell_sec: number | null;
  p90_dwell_sec: number | null;
  /** Cohort members still sitting in this stage (excluded from the dwell percentile). */
  censored: number;
  /** Cohort members who re-entered this stage (pivots/re-screens). */
  reentry: number;
}

/** One recruiter's productivity totals + per-day grid (GET /analytics/productivity). */
export interface ProductivityRecruiter {
  user_id: string;
  name: string | null;
  /** metric -> total over the range. */
  totals: Record<string, number>;
  /** 'YYYY-MM-DD' -> { metric -> n }. */
  by_day: Record<string, Record<string, number>>;
}

/** GET /api/crm/analytics/productivity — per-recruiter x per-day activity rollup. */
export interface ProductivityAnalytics {
  range: { from: string | null; to: string | null };
  /** IANA tz used for day bucketing (default America/New_York). */
  tz: string;
  /** Metric keys in display order (mirrors the backend PRODUCTIVITY_METRICS). */
  metrics: string[];
  /** Dense ascending day axis ('YYYY-MM-DD') present in the data. */
  days: string[];
  recruiters: ProductivityRecruiter[];
  totals: Record<string, number>;
  study_scoped?: boolean;
}

/** One recruiter's disposition counts (GET /analytics/dispositions). */
export interface DispositionRecruiter {
  user_id: string;
  name: string | null;
  /** lifecycle to_status -> distinct leads moved there in range. */
  counts: Record<string, number>;
  /** Sum across all matrix statuses. */
  total: number;
  /** Sum across the negative dispositions only. */
  negative_total: number;
}

/** GET /api/crm/analytics/dispositions — recruiter × disposition matrix. */
export interface DispositionAnalytics {
  range: { from: string | null; to: string | null };
  /** The matrix columns, in display order (positives then negatives). */
  statuses: string[];
  /** The subset of `statuses` that are terminal-negative (reason-drillable). */
  negative_statuses: string[];
  recruiters: DispositionRecruiter[];
  totals: Record<string, number>;
  grand_total: number;
  study_scoped?: boolean;
}

/** GET /api/crm/analytics/dispositions/reasons row. */
export interface DispositionReasonRow {
  reason_code: string;
  total: number;
}

/** GET /api/crm/analytics/dispositions/reason-contacts row (one lead). */
export interface DispositionContact {
  contact_id: string;
  contact: Pick<Contact, 'id' | 'full_name' | 'first_name' | 'last_name'> | null;
  reason_code: string;
  reason_note: string | null;
  source: string;
  occurred_at: string;
}

/** GET /api/crm/analytics/screening/:gate/verdicts row. */
export interface ScreeningVerdictRow {
  /** eligible | needs_review | not_eligible | pending | incomplete | unknown. */
  verdict: string;
  total: number;
}

/** GET /api/crm/analytics/screening/:gate/reasons row — one driver. */
export interface ScreeningReasonRow {
  question_key: string;
  /** The question text (stored on the reason). */
  label: string;
  /** 'exclude' (drove not-eligible) | 'review' (drove needs-review). */
  kind: string;
  /** The answer that triggered it (Yes/No, a number, text, or '(no answer)'). */
  answer_value: string;
  total: number;
}

/** GET …/screening/:gate/daily row — leads on one local day in one verdict. */
export interface ScreeningDailyRow {
  /** 'YYYY-MM-DD' (bucketed in the report tz). */
  day: string;
  verdict: string;
  total: number;
}

/** GET …/screening/intake/sources row — landing leads by source x verdict. */
export interface ScreeningSourceRow {
  lead_source: string;
  verdict: string;
  total: number;
}

/** One lead behind a driver (GET …/reason-contacts). The answer is PHI (audited). */
export interface ScreeningReasonContact {
  contact_id: string;
  contact: Pick<Contact, 'id' | 'full_name' | 'first_name' | 'last_name'> | null;
  answer_value: string;
  occurred_at: string;
}

/** GET /api/crm/analytics/sales-summary — headline KPIs for one creation-cohort. */
export interface SalesSummary {
  leads: number;
  reached: number;
  reach_pct: number | null;
  speed_median_sec: number | null;
  speed_p90_sec: number | null;
  speed_within_15min_pct: number | null;
  outbound_calls: number;
  connected_calls: number;
  connect_pct: number | null;
  randomized: number;
  lead_to_rand_pct: number | null;
  cycle_median_days: number | null;
}

/** Cross-study answer vocabulary entry (pivot engine; admin-curated). */
export interface ProfileKey {
  id: string;
  key: string;
  label: string;
  value_type: string;
  unit: string | null;
  options: any;
  description: string | null;
  /** draft | active | archived. */
  status: string;
}

/** GET /api/crm/contacts/stats — pipeline counts for the inbox tabs. */
export interface LifecycleStats {
  total: number;
  unassigned: number;
  by_status: Record<string, number>;
  /** Conversation-tab counts (sidebar badges). */
  conversations?: {
    active: number;
    awaiting: number;
    needs_reply: number;
    archived: number;
  };
  /** Unmatched inbound faxes awaiting triage (Records sidebar badge, Phase E). */
  fax_needs_match?: number;
  /** Unheard voicemails (Phone sidebar badge, Phase 4c). */
  voicemail_unheard?: number;
}

// ---- Phone tab (Phase 4c) ---------------------------------------------------

/** A voicemail row for the Phone tab inbox (GET /api/crm/voice/voicemails). */
export interface Voicemail {
  id: string;
  contact_id: string | null;
  name: string | null;
  from: string | null;
  duration_sec: number | null;
  occurred_at: string;
  heard_at: string | null;
  heard_by: string | null;
}

/** A recent call row for the Phone activity log (GET /api/crm/voice/calls). */
export interface CallActivity {
  id: string;
  contact_id: string | null;
  name: string | null;
  direction: 'inbound' | 'outbound' | string;
  status: string | null;
  occurred_at: string;
  from: string | null;
  /** Recruiter who placed (outbound) or answered (inbound) the call, if known. */
  agent: string | null;
  duration_sec: number | null;
  voicemail: boolean;
}

/** One open-office-hours schedule (after-hours auto-text, item #3). */
export interface AfterHoursSchedule {
  /** Open weekdays, 0=Sun..6=Sat. */
  days: number[];
  /** Open time, 'HH:MM' (24h). */
  open: string;
  /** Close time, 'HH:MM' (24h). */
  close: string;
}

/** Editable phone settings (GET/PUT /api/crm/voice/settings). */
export interface VoiceSettings {
  greeting: string;
  /** True when a staff-recorded greeting is stored (played instead of TTS). */
  has_greeting_audio: boolean;
  hold_music_url: string;
  owner_ring_sec: number;
  group_ring_sec: number;
  voicemail_max_sec: number;
  voicemailEnabled: boolean;
  callTransferEnabled: boolean;
  /** After-hours auto-text settings (item #3). Dormant until `enabled`. */
  after_hours?: {
    enabled: boolean;
    default: AfterHoursSchedule;
    sites: Record<string, AfterHoursSchedule>;
    message: string;
  };
}

// ---- Records release + e-sign + fax (Expansion Phase E) ---------------------

/** Present while a busy/no-answer fax is auto-retrying (queued/sending, attempt > 1). */
export interface FaxAutoRetry {
  attempt: number;
  max: number;
}

/** Latest outbound fax for a signature request (RecordsCard). */
export interface RecordsFax {
  id: string;
  /** queued | sending | delivered | failed. */
  status: string;
  to_number: string | null;
  error: string | null;
  page_count: number | null;
  updated_at: string;
  /** Non-null while the system is auto-retrying a transient failure (e.g. busy line). */
  auto_retry?: FaxAutoRetry | null;
}

/** Latest outbound fax to ONE office for a request (Phase E.2 — multi-office). */
export interface RecordsOfficeFax {
  to_number: string | null;
  name: string | null;
  /** queued | sending | delivered | failed. */
  status: string;
  error: string | null;
  updated_at: string | null;
  /** Non-null while the system is auto-retrying a transient failure (e.g. busy line). */
  auto_retry?: FaxAutoRetry | null;
}

/** One row of GET /api/crm/contacts/:id/records-requests. */
export interface RecordsRequest {
  id: string;
  /** created | sent | viewed | signed | expired | cancelled (lazily expired). */
  status: string;
  created_at: string;
  expires_at: string;
  sent_at: string | null;
  viewed_at: string | null;
  signed_at: string | null;
  signer_name: string | null;
  /** Null until the patient names the office(s); then a first-office summary. */
  doctor_name: string | null;
  doctor_fax: string | null;
  /** Recruiter-chosen records timespan (6m/12m/3y/5y/all) + its label. */
  lookback: string | null;
  lookback_label: string | null;
  /** The office(s) the patient named (empty until signed); fax/location added
   * by the recruiter at send time. */
  offices: { name: string; city_state: string; fax: string | null }[];
  hospital: { name: string; city_state: string; fax: string | null } | null;
  pharmacy: { name: string; city_state: string; fax: string | null } | null;
  document_id: string | null;
  signed_document_id: string | null;
  /** The overall latest outbound fax (back-compat); per-office statuses in `faxes`. */
  fax: RecordsFax | null;
  faxes: RecordsOfficeFax[];
  received: { document_id: string; filename: string; created_at: string }[];
}

/** A per-contact PHI file (crm.documents). Medical records shown on the lead
 * profile are doc_type 'medical_record': faxed in (source 'fax_inbound'),
 * dropped straight onto the profile by a recruiter (source 'upload'), or
 * combined from several records (source 'merged'). */
export interface ContactDocument {
  id: string;
  /** release_unsigned | release_signed | medical_record | cover_sheet | other. */
  doc_type: string;
  filename: string;
  mime_type: string | null;
  file_size: number | null;
  /** generated | esign | fax_inbound | sms_inbound | upload | merged. */
  source: string;
  created_at: string;
}

/** Derived delivery stats for a provider office (from crm.fax_jobs by fax). */
export interface ProviderOfficeStats {
  sent: number;
  delivered: number;
  failed: number;
  last_faxed_at: string | null;
}

/** One row of GET /api/crm/provider-offices — the reusable fax directory:
 * an office, the doctors who practice there, ONE fax, location, notes, a manual
 * 1-5 rating, and derived fax-delivery stats. */
export interface ProviderOffice {
  id: string;
  network_id: string;
  office_name: string;
  doctor_names: string[];
  fax_number: string | null;
  location: string | null;
  notes: string | null;
  /** Manual 1-5 reliability rating (null = unrated). */
  rating: number | null;
  created_at: string;
  updated_at: string;
  stats: ProviderOfficeStats;
}

/** GET /api/crm/fax/inbox row (triage queue). */
export interface FaxInboxJob {
  id: string;
  from_number: string | null;
  to_number: string | null;
  page_count: number | null;
  created_at: string;
  document: { id: string; filename: string; file_size: number | null } | null;
  candidates: { id: string; name: string; phone: string | null }[];
}

/** GET /api/crm/fax row — one fax (either direction) in the history list. */
export interface FaxRecord {
  id: string;
  direction: 'outbound' | 'inbound';
  /** outbound: queued|sending|delivered|failed; inbound: received|needs_match|matched|discarded */
  status: string;
  to_number: string | null;
  from_number: string | null;
  reference_code: string | null;
  page_count: number | null;
  error: string | null;
  created_at: string;
  updated_at: string;
  contact: { id: string; name: string } | null;
  /** The viewable PDF (inbound media, or the outbound merged cover+doc). */
  document_id: string | null;
  document_filename: string | null;
  is_standalone: boolean;
  note: string | null;
}

/** GET /api/crm/fax/config — the live sending fax number, for display only. */
export interface FaxConfig {
  from_number: string | null;
  from_number_display: string | null;
  configured: boolean;
}

export interface LeadLink {
  id: string;
  lead_id: string;
  study_slug: string | null;
  lead_source?: string | null;
  eligibility_status: string | null;
  first_seen_at: string;
  lead_snapshot?: any;
}

export interface RawAnswer {
  question_id: string;
  question_type: string | null;
  value_text: string | null;
  value_num: number | null;
  value_bool: boolean | null;
  value_json: unknown;
}

export interface ScreenerAnswer {
  question_id: string;
  label: string;
  type: string | null;
  value: unknown;
  display: string;
  /** 'exclude' (disqualifying → red), 'review' (flagged → yellow), or null. */
  eligibility_kind?: 'exclude' | 'review' | null;
}

export interface ScreenerSubmission {
  lead_id: string;
  study_slug: string | null;
  study_title: string | null;
  /** Recruiter-only internal alias overlaid live (null = none); render
   *  `study_internal_title || study_title` in recruiter views. */
  study_internal_title?: string | null;
  questionnaire_name: string | null;
  eligibility_status: string | null;
  eligibility_notes: string | null;
  incomplete: boolean;
  first_seen_at: string | null;
  answer_count: number;
  answers: ScreenerAnswer[];
}

export interface Screener {
  submissions: ScreenerSubmission[];
  incompleteCount: number;
}

export interface Enrollment {
  id: string;
  flow_id: string;
  flow_name?: string | null;
  flow_version: number;
  status: string;
  current_node_id: string | null;
  next_run_at: string | null;
  enrolled_at: string;
  completed_at: string | null;
  last_error: string | null;
}

/** Follow-up task (crm.tasks). Returned by GET /api/crm/tasks. */
export interface Task {
  id: string;
  network_id?: string;
  contact_id: string | null;
  assignee_user_id: string | null;
  title: string;
  description: string | null;
  due_at: string | null;
  /** open | in_progress | done | cancelled (unknowns tolerated). */
  status: string;
  /** low | normal | high | urgent (unknowns tolerated). */
  priority: string;
  created_by?: string | null;
  created_at: string;
  updated_at?: string;
  /** Slim contact summary embedded on the list endpoint (null for orphans). */
  contact?: Pick<Contact, 'id' | 'full_name' | 'first_name' | 'last_name' | 'phone_e164'> | null;
}

/** Scheduled callback (crm.scheduled_callbacks). */
export interface ScheduledCallback {
  id: string;
  scheduled_at: string;
  duration_min: number;
  /** booked | completed | cancelled | no_show (unknowns tolerated). */
  status: string;
  /** recruiter | public_link. */
  booked_via: string;
  note: string | null;
  assignee_user_id: string | null;
  created_by?: string | null;
  created_at?: string;
}

/** Callback row on the worklist (carries a slim contact). */
export interface WorklistCallback extends ScheduledCallback {
  contact_id: string;
  contact: Pick<Contact, 'id' | 'first_name' | 'last_name' | 'full_name' | 'phone_e164' | 'lifecycle_status' | 'owner_user_id'>;
}

/** GET /api/crm/callbacks/upcoming — slim feed for the in-app reminder host. */
export interface UpcomingCallback {
  id: string;
  contact_id: string;
  scheduled_at: string;
  /** First name only — no PHI beyond the given name. */
  first_name: string | null;
}

export interface WorklistBucket<T> {
  count: number;
  /** Absent in counts-only mode. */
  items?: T[];
}

/** GET /api/crm/worklist — the recruiter's bucketed daily to-do list. */
export interface WorklistResponse {
  generated_at: string;
  max_attempts: number;
  stale_days: number;
  tasks_due: number;
  total_actionable: number;
  buckets: {
    callbacks: WorklistBucket<WorklistCallback>;
    needs_reply: WorklistBucket<ContactListItem>;
    new_leads: WorklistBucket<ContactListItem>;
    follow_ups: WorklistBucket<ContactListItem>;
    stale: WorklistBucket<ContactListItem>;
  };
}

/**
 * GET /api/crm/follow-ups — cadence follow-ups due by end of the viewer's local
 * day (incl. overdue), the sortable/filterable "needs follow up" list on Today.
 * `scope` echoes the requested mine|everyone; `truncated` is set when more rows
 * matched than the server's cap returned (narrow with filters to see the rest).
 */
export interface FollowUpsDueResponse {
  generated_at: string;
  max_attempts: number;
  scope: 'mine' | 'everyone';
  count: number;
  truncated: boolean;
  items: ContactListItem[];
}

// ---------------------------------------------------------------------------
// My Calendar (aggregate of callbacks + tasks + personal events)
// ---------------------------------------------------------------------------

export type CalendarSourceKind = 'callback' | 'task' | 'event';

/**
 * One normalized item on the recruiter calendar. `editable` is true only for
 * personal events (callbacks/tasks are managed from the contact / worklist).
 * `start`/`end` are ISO; callbacks carry the patient name in `title` (in-app,
 * behind auth — the de-identification rule applies only to the Google mirror).
 */
export interface CalendarItem {
  source_kind: CalendarSourceKind;
  source_id: string;
  /** Recruiter this item belongs to (callback/task assignee, or the viewer for
   *  personal events). null = unassigned callback. Drives per-person coloring
   *  when several recruiters' calendars are overlaid. */
  owner_user_id?: string | null;
  title: string;
  start: string;
  end: string;
  all_day: boolean;
  status: string | null;
  editable: boolean;
  contact_id?: string | null;
  contact_name?: string | null;
  booked_via?: string | null;
  note?: string | null;
  priority?: string | null;
  location?: string | null;
  description?: string | null;
  color?: string | null;
  /** Recurrence rule for personal events: null = single, 'weekly' = repeats. */
  recurrence?: 'weekly' | null;
  /** Weekdays a weekly series repeats on (0=Sun … 6=Sat). */
  recurrence_days?: number[] | null;
  /** Inclusive end date (YYYY-MM-DD) of a recurring series; null = open-ended. */
  recurrence_until?: string | null;
  /** Personal events only: whether this block makes the recruiter unavailable
   *  (blocks lead bookings + new-lead routing). Defaults true. */
  blocks_booking?: boolean;
  /** Unique per rendered instance (set when a recurring base is expanded into
   *  occurrences, so React keys stay unique). Falls back to source_id. */
  instance_key?: string;
}

/** GET /api/crm/calendar response. */
export interface CalendarResponse {
  events: CalendarItem[];
  range: { from: string; to: string };
}

/** crm.user_calendar_events row — a personal event the caller owns. */
export interface UserCalendarEvent {
  id: string;
  network_id: string;
  user_id: string;
  title: string;
  description: string | null;
  starts_at: string;
  ends_at: string;
  all_day: boolean;
  location: string | null;
  color: string | null;
  recurrence: 'weekly' | null;
  recurrence_days: number[] | null;
  recurrence_until: string | null;
  blocks_booking: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface Communication {
  id: string;
  channel: string;
  direction: string | null;
  status: string | null;
  body: string | null;
  user_id: string | null;
  external_provider: string | null;
  occurred_at: string;
  metadata: any;
}

/** The contact's pinned primary study, resolved on the detail response. */
export interface PrimaryStudyRef {
  id: string;
  slug: string;
  title: string;
  /** Recruiter-only internal alias (null = none); render via studyInternalLabel. */
  internal_title?: string | null;
}

/**
 * Per-study eligibility verdict (#08), rolled up from study_attempts on the
 * detail response. `attempt_status` is the raw journey status (badge via
 * attemptStatusMeta); `eligibility` is the compact verdict the inbox filter
 * shares (eligible | screening | not_eligible | withdrew).
 */
export interface StudyEligibilityRow {
  study_id: string;
  study_slug: string | null;
  study_title: string | null;
  attempt_status: string;
  eligibility: string;
  started_at: string | null;
  outcome_at: string | null;
}

export interface ContactDetailResponse {
  contact: Contact;
  leads: LeadLink[];
  answers: RawAnswer[];
  screener: Screener;
  /** Resolved label for contact.primary_study_id (null when none pinned). */
  primary_study?: PrimaryStudyRef | null;
  /** Per-study eligibility chips (#08); empty when no screening journey yet. */
  study_eligibility?: StudyEligibilityRow[] | null;
  /** Tenant cadence ceiling for "attempt N of M" copy (defaults to 6). */
  max_attempts?: number;
  /** Phase G: six-probe completeness meter (null when the probe failed). */
  profile_completeness?: ProfileCompleteness | null;
  /** Phase G: header ratings chip (null when the probe failed). */
  rating_summary?: RatingSummary | null;
}

// ---------------------------------------------------------------------------
// Medical profile + interaction ratings (Expansion Phase G)
// ---------------------------------------------------------------------------

/** A contact medication or condition row (crm.contact_medications/_conditions). */
export interface ProfileItem {
  id: string;
  contact_id: string;
  name: string;
  name_normalized: string;
  /** Medications only. */
  dose?: string | null;
  frequency?: string | null;
  /** meds: active | stopped | unknown; conditions: active | resolved | unknown. */
  status: string;
  /** form | recruiter | ai_extraction | lead. */
  source: string;
  source_ref?: string | null;
  profile_key?: string | null;
  /** Provenance for AI-extracted facts (the call/text/fax it came from). */
  source_communication_id?: string | null;
  source_document_id?: string | null;
  /** Provenance for form-derived facts (the completed form response). */
  source_form_response_id?: string | null;
  source_excerpt?: string | null;
  /** AI extraction confidence: high | medium | low (null for recruiter-entered). */
  confidence?: string | null;
  verified_by: string | null;
  verified_at: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at?: string;
}

/** A contact lab/test result row (crm.contact_lab_results). */
export interface LabResult {
  id: string;
  contact_id: string;
  test_name: string;
  test_name_normalized: string;
  value_num?: number | null;
  value_text?: string | null;
  unit?: string | null;
  reference_low?: number | null;
  reference_high?: number | null;
  /** low | high | normal | abnormal | unknown. */
  abnormal_flag?: string | null;
  /** e.g. fasting | random | postprandial. */
  specimen_context?: string | null;
  result_date?: string | null;
  date_precision?: string | null;
  /** form | recruiter | ai_extraction | lead. */
  source: string;
  source_ref?: string | null;
  source_communication_id?: string | null;
  source_document_id?: string | null;
  source_form_response_id?: string | null;
  source_excerpt?: string | null;
  confidence?: string | null;
  profile_key?: string | null;
  verified_by: string | null;
  verified_at: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at?: string;
}

/** Append-only interaction rating (crm.interaction_ratings). */
export interface InteractionRating {
  id: string;
  contact_id: string;
  recruiter_user_id: string | null;
  rating: number;
  note: string | null;
  communication_id: string | null;
  created_at: string;
}

export interface RatingSummary {
  /** Mean to 1 decimal; null until the first rating. */
  average: number | null;
  count: number;
}

export interface ProfileCompleteness {
  percent: number;
  /** Stable keys: phone | email | medications | conditions | completed_form | answers. */
  missing: string[];
}

/** Best display name for a contact (full_name, else first+last, else placeholder). */
export function contactName(c: Pick<Contact, 'full_name' | 'first_name' | 'last_name'>): string {
  return c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || '(no name)';
}

/** A study-shaped object carrying the recruiter (internal) and/or public title. */
export interface StudyLabelParts {
  title?: string | null;
  internal_title?: string | null;
  slug?: string | null;
}

/**
 * Recruiter-facing study label: the admin-only internal title when set, else the
 * public title (then slug). Use everywhere INSIDE the CRM where a recruiter/admin
 * reads a study name. NEVER use for patient-facing copy.
 */
export function studyInternalLabel(s: StudyLabelParts): string {
  return s.internal_title || s.title || s.slug || '';
}

/**
 * Public-facing study label: the public `title` (then slug). Use for what a
 * patient sees — outbound-message previews, the public website. Never the
 * internal title.
 */
export function studyPublicLabel(s: StudyLabelParts): string {
  return s.title || s.slug || '';
}

// ---------------------------------------------------------------------------
// Forms engine (Expansion Phase C)
// ---------------------------------------------------------------------------

/** Branching condition: hide the question until the referenced answer matches. */
export interface FormShowIf {
  question_key: string;
  /** equals | not_equals | contains | greater_than | less_than. */
  operator: string;
  value: string | number | boolean;
}

/** WCR-compatible qualification rule on one question. */
export interface FormEligibilityRule {
  exclude_if?: { operator: string; value: any } | null;
  flag_for_review_if?: { operator: string; value: any } | null;
}

/**
 * Optional tag: an affirmative/recorded answer to this question is written as a
 * structured medical fact on the contact's profile (so cohort queries like
 * "type 2 diabetes" / "BMI > 30" capture the lead). Mirrors crmForms.MEDICAL_FACT_KINDS.
 */
export interface FormMedicalFact {
  /** condition | medication | lab — the destination profile table. */
  kind: 'condition' | 'medication' | 'lab';
  /** The canonical fact recorded (e.g. "Type 2 diabetes"). */
  name?: string;
  /** Precise cross-study key (e.g. condition.t2dm) for exact querying. */
  profile_key?: string;
  /** Override the default "is this affirmative?" test (default: yes = Yes, numeric = a value). */
  positive_when?: { operator: string; value: any };
}

/** One question in a form definition (crm.forms.definition / form_versions). */
export interface FormQuestionDef {
  /** Immutable. Builder-minted `form.*` slug or an imported WCR question id. */
  key: string;
  /** text|textarea|email|phone|number|height|date|single_choice|multiple_choice|yes_no|scale|calculation. */
  type: string;
  label: string;
  required?: boolean;
  description?: string | null;
  placeholder?: string | null;
  options?: { value: string; label: string }[] | null;
  min?: number | null;
  max?: number | null;
  show_if?: FormShowIf | null;
  eligibility?: FormEligibilityRule | null;
  calculation?: { preset: string; inputs: Record<string, string> } | null;
  medical_fact?: FormMedicalFact | null;
}

export interface FormDefinition {
  questions: FormQuestionDef[];
  /** Outcome messages, greeting, Phase I passthrough — bounded scalars/arrays. */
  settings: Record<string, any>;
}

/** A form row (list + detail). */
export interface CrmForm {
  id: string;
  study_id: string | null;
  /** comprehensive | lead_gen | custom (legacy secondary tag; `surface` is primary). */
  kind: string;
  /** Item #05 group: 'landing' (WCR ad page) | 'questionnaire' (texted /f link). */
  surface?: string;
  title: string;
  description: string | null;
  therapeutic_area: string | null;
  /** draft | active | archived. */
  status: string;
  /** Current published version (0 = never published). */
  version: number;
  definition?: FormDefinition;
  imported_questionnaire_id?: string | null;
  import_meta?: any;
  created_at: string;
  updated_at: string;
  counts?: { responses: number; completed: number };
}

export interface FormVersionSummary {
  id: string;
  version: number;
  published_by: string | null;
  published_at: string;
}

// ---- Automation flows (Part 6 builder) -------------------------------------

/** Wait/timeout duration: a number of SECONDS or day/hour parts. */
export type FlowDuration = number | { days?: number; hours?: number; minutes?: number; seconds?: number };

export interface FlowConditionDef {
  when: { field?: string; op?: string; value?: any };
  goto?: string | null;
}

/** One node of a flow definition — plan §6.11 is the authoritative schema. */
export interface FlowNodeDef {
  type: string;
  /** Edge to the next node; null/omitted completes the enrollment. */
  next?: string | null;
  // send_sms / send_email
  body?: string;
  subject?: string;
  quiet_hours_exempt?: boolean;
  // wait
  duration?: FlowDuration;
  until_business_hours?: boolean;
  timezone?: string;
  // wait_for_reply
  timeout?: FlowDuration;
  on_timeout?: string | null;
  // branch
  conditions?: FlowConditionDef[];
  else?: string | null;
  // create_task
  title?: string;
  description?: string | null;
  priority?: string;
  // update_contact
  set?: Record<string, any>;
}

export interface FlowDefinition {
  /** Explicit entry node id — the builder always writes it (§6.11). */
  entry: string | null;
  nodes: Record<string, FlowNodeDef>;
}

/** An automation_flows row (list + builder). */
export interface CrmFlow {
  id: string;
  name: string;
  description: string | null;
  /** draft | active | archived. */
  status: string;
  /** Current published version (0 = never published). */
  version: number;
  trigger_type: string;
  trigger_config: Record<string, string | string[]>;
  enrollment_rules: { reenroll?: boolean; dedupe_window_hours?: number };
  definition?: FlowDefinition;
  created_at: string;
  updated_at: string;
  counts?: { active_enrollments: number };
}

export interface FlowVersionSummary {
  id: string;
  version: number;
  published_by: string | null;
  published_at: string;
}

/** A contact's texted form link (GET /contacts/:id/form-links). */
export interface ContactFormLink {
  id: string;
  form_id: string;
  form_title: string;
  form_kind: string | null;
  /** created | sent | started | completed | expired | cancelled (display-derived). */
  status: string;
  expires_at: string;
  sent_at: string | null;
  created_at: string;
  url: string;
  response: {
    id: string;
    status: string;
    eligibility_status: string | null;
    completed_at: string | null;
  } | null;
}

/** Label-resolved response detail (GET /form-responses/:id). */
export interface FormResponseDetail {
  id: string;
  contact_id: string;
  form_id: string;
  form_title: string;
  version: number | null;
  status: string;
  eligibility_status: string | null;
  eligibility_reasons: { question_key: string; kind: string; label: string }[] | null;
  started_at: string;
  completed_at: string | null;
  answers: { question_key: string; label: string; type: string | null; display: string }[];
}

// ---------------------------------------------------------------------------
// AI eligibility review (CRM medical history review)
// ---------------------------------------------------------------------------

/**
 * Pipeline status of one review (crm.eligibility_reviews.status).
 * 'extracting_facts' / 'evaluating' are facts-engine-only in-flight states
 * (legacy reviews never pass through them; additive to the CHECK constraint).
 */
export type EligibilityReviewStatus =
  | 'queued'
  | 'building_dossier'
  | 'analyzing'
  | 'verifying'
  | 'extracting_facts'
  | 'evaluating'
  | 'completed'
  | 'failed'
  | 'cancelled';

/** Overall AI verdict for one contact × study review. */
export type EligibilityOverallResult = 'eligible' | 'excluded' | 'review_required' | 'incomplete';

/**
 * Per-criterion verdict (MHR's RESULT_TYPES + the CRM-only 'not_applicable' —
 * a criterion that structurally cannot apply to this patient, e.g. a
 * female-only requirement for a male lead; always eligible-impact).
 */
export type EligibilityCriterionResultValue = 'met' | 'not_met' | 'uncertain' | 'not_found' | 'contradictory' | 'not_applicable';

/**
 * Segment-coverage stats stored on counts.coverage — how much of the dossier
 * the criterion citations touched, and whether the uncited-segment
 * verification pass ran (it only runs when >5% of the corpus went uncited).
 */
export interface EligibilityCoverage {
  segments_total?: number;
  segments_cited?: number;
  total_chars?: number;
  uncited_chars?: number;
  verification_ran?: boolean;
}

/**
 * Provenance of one dossier segment (legacy) or fact/citation (facts engine)
 * — where a piece of the patient's story came from. `document_page` sources
 * resolve to a real medical-record page (the segment panel / PDF jump offers
 * "Open document"); the rest point at comms/forms/profile/demographics. `lead`
 * is facts-engine-only (a structured answer recorded at intake).
 */
export interface EligibilitySourceRef {
  type: 'document_page' | 'communication' | 'form_response' | 'attempt' | 'profile' | 'demographics' | 'lead';
  document_id?: string;
  page?: number;
  communication_id?: string;
  lead_id?: string;
  form_response_id?: string;
  attempt_id?: string;
  occurred_at?: string;
  label: string;
}

/**
 * One numbered segment of the contact dossier
 * (GET /api/crm/eligibility-reviews/:id/segments/:n → { segment }).
 */
export interface DossierSegment {
  n: number;
  label: string;
  source: EligibilitySourceRef;
  text: string;
}

/**
 * One citation on a criterion result — points at a dossier segment (legacy
 * engine) OR, for the facts engine, has no segment at all: `segment_number`
 * is null and the citation is rendered directly from `excerpt` + `source`
 * (no `/segments/:n` fetch). `fact_id` is present when the citation came
 * from a `contact_facts` row (facts engine only).
 */
export interface EligibilityCitation {
  segment_number: number | null;
  /** ≤500-char excerpt of the evidence text. */
  excerpt: string;
  evidence_type: 'supports' | 'contradicts' | 'mentions';
  source: EligibilitySourceRef;
  /** Facts engine only — the contact_facts row this citation resolved from. */
  fact_id?: string;
}

/**
 * Per-criterion AI evaluation (crm.eligibility_criterion_results), joined with
 * the criterion's text/kind/number/category on the review detail endpoint.
 */
export interface EligibilityCriterionResult {
  id: string;
  review_id: string;
  criterion_id: string;
  result: EligibilityCriterionResultValue;
  confidence_score: number | null;
  confidence_level: 'high' | 'medium' | 'low' | 'unable' | null;
  confidence_factors: any;
  ai_reasoning: string | null;
  has_contradiction: boolean;
  contradiction_severity: string | null;
  from_verification_pass: boolean;
  requires_human_review: boolean;
  citations: EligibilityCitation[];
  human_verified: boolean;
  human_result: EligibilityCriterionResultValue | null;
  human_override_reason: string | null;
  human_notes: string | null;
  verified_by: string | null;
  verified_at: string | null;
  created_at: string;
  // Joined from crm.study_review_criteria on the detail endpoint.
  criterion_kind?: 'inclusion' | 'exclusion';
  criterion_number?: string;
  category?: string;
  criterion_text?: string;
  plain_language?: string | null;
  is_critical?: boolean;
  // ---- Facts engine (Phase 3) — how this verdict was reached ----
  /** 'deterministic' (Tier 0, code-only) | 'llm_small' (Tier 1, Haiku) |
   *  'llm_large' (Tier 2, Sonnet) | 'legacy' | null (legacy rows / not set). */
  resolution_tier?: 'deterministic' | 'llm_small' | 'llm_large' | 'legacy' | null;
  /** Facts-engine retrieval transparency: {terms, fact_hits, snippet_hits, pages, …}. */
  retrieval_meta?: {
    terms?: string[];
    fact_hits?: number;
    snippet_hits?: number;
    answer_hits?: number;
    pages?: number[];
    escalations?: string[];
    [key: string]: unknown;
  } | null;
}

/**
 * One AI eligibility review of a contact against one study
 * (crm.eligibility_reviews row; list rows add study_title +
 * criteria_set_version).
 */
export interface EligibilityReview {
  id: string;
  network_id?: string;
  contact_id: string;
  study_id: string;
  /** Groups the reviews started by one button click. */
  batch_id: string;
  dossier_id: string | null;
  criteria_set_id: string;
  status: EligibilityReviewStatus;
  overall_result: EligibilityOverallResult | null;
  summary: string | null;
  /** {met, not_met, uncertain, not_found, contradictory, not_applicable, exclusions_met, coverage?, extraction? (facts engine)}. */
  counts: {
    met?: number;
    not_met?: number;
    uncertain?: number;
    not_found?: number;
    contradictory?: number;
    not_applicable?: number;
    exclusions_met?: number;
    coverage?: EligibilityCoverage;
    /** Facts engine only — per-document fact-extraction outcomes for this
     *  review's contact (there is no dossier to report coverage on instead). */
    extraction?: {
      documents_total?: number;
      documents_ok?: number;
      documents_failed?: { document_id: string; reason?: string; filename?: string }[];
      facts_used?: number;
    };
    /** Facts engine only — echoes review.engine for callers reading counts alone. */
    engine?: 'legacy' | 'facts';
    [key: string]: unknown;
  } | null;
  criteria_total: number | null;
  criteria_evaluated: number | null;
  estimated_cost: number | null;
  actual_cost: number | null;
  input_tokens: number;
  output_tokens: number;
  cached_input_tokens: number;
  cache_write_tokens: number;
  model_id: string | null;
  error: string | null;
  requested_by: string | null;
  signed_off_by: string | null;
  signed_off_at: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  /** Recruiter-facing study label (internal-title fallback), on the list rows. */
  study_title?: string | null;
  criteria_set_version?: number | null;
  /** List rows: true when any criterion result carries a human override. */
  has_overrides?: boolean;
  /** Prompt/model provenance recorded when the review ran (null for older runs). */
  prompt_meta?: EligibilityPromptRunMeta | null;
  /** Which evaluation engine ran this review — 'legacy' (default; whole-dossier
   *  Sonnet analysis) or 'facts' (compiled rules + tiered retrieval). Null on
   *  older rows predating the column — treat as legacy. */
  engine?: 'legacy' | 'facts' | null;
  /** Recruiter-chosen evidence scope this review ran against (source-scoping
   *  addendum). NULL/absent = legacy "all sources". Identity + recruiter-entered
   *  profile facts are always included regardless. */
  source_selection?: {
    medical_records?: { enabled?: boolean; document_ids?: string[] };
    communications?: { enabled?: boolean };
    forms?: { enabled?: boolean };
  } | null;
}

/** A dossier source that could not be included in the AI's reading. */
export interface EligibilityExcludedSource {
  type: string;
  id?: string;
  reason?: string;
  detail?: string;
}

/** GET /api/crm/eligibility-reviews/:id — the full results-page payload. */
export interface EligibilityReviewDetailResponse {
  review: EligibilityReview;
  results: EligibilityCriterionResult[];
  dossier: {
    segment_count: number | null;
    excluded_sources: EligibilityExcludedSource[];
    built_at: string | null;
  } | null;
  /** Source documents for the results-page viewer (faxed + uploaded records). */
  documents?: EligibilityReviewDocument[];
  /** §3.5 staleness: the criteria set derives from eReg document versions that
   *  have since been superseded — the queue shows an informational line. */
  criteria_source_stale?: boolean;
}

/**
 * One source document available in the results-page split-view source viewer
 * (the detail response's `documents` array). Bytes come from the existing
 * GET /api/crm/documents/:id/file proxy, never a presigned URL.
 */
export interface EligibilityReviewDocument {
  id: string;
  filename: string | null;
  page_count: number | null;
  source: string | null;
  created_at: string | null;
}

/** Which version of one prompt a review ran with (review.prompt_meta). */
export interface EligibilityPromptVersionRef {
  version: number | null;
  source: string | null;
}

/**
 * Prompt/model provenance recorded at run time — which prompt versions and
 * model actually produced this review's results. Facts-engine rows key by
 * `tier1_judge`/`tier2_judge`; rows from the retired legacy engine carry
 * `review_system`/`criterion_analysis` instead (verbatim, era-specific keys —
 * never both). Typed loosely (all keys optional) so ONE type covers both eras;
 * always check for the era's actual keys before reading a version ref.
 */
export interface EligibilityPromptRunMeta {
  /** Facts engine (current). */
  tier1_judge?: EligibilityPromptVersionRef;
  tier2_judge?: EligibilityPromptVersionRef;
  /** Legacy engine (retired 2026-07-09) — historical rows only. */
  review_system?: EligibilityPromptVersionRef;
  criterion_analysis?: EligibilityPromptVersionRef;
  model_id?: string | null;
  recorded_at?: string | null;
}

/** One prompt as currently configured in the admin-editable registry. */
export interface EligibilityPromptCurrent {
  key: string;
  text: string;
  version: number | null;
  source: string | null;
  max_tokens?: number | null;
}

/**
 * GET /api/crm/eligibility-reviews/:id/prompts — current vs ran prompt info.
 * `current` is always the two live facts-engine judge prompts, regardless of
 * which engine the review itself ran on (there is no "current legacy prompt"
 * anymore). `ran` is a VERBATIM passthrough of the review's stored
 * `prompt_meta` — null when the run predates prompt-version recording, and,
 * for legacy-era reviews, keyed by `review_system`/`criterion_analysis`
 * rather than `tier1_judge`/`tier2_judge` (see EligibilityPromptRunMeta).
 */
export interface EligibilityReviewPromptsResponse {
  review: EligibilityReview;
  /** Which engine ran this review ('facts' | 'legacy' | null for older rows). */
  engine: EligibilityReview['engine'];
  current: {
    tier1_judge: EligibilityPromptCurrent;
    tier2_judge: EligibilityPromptCurrent;
  };
  ran: EligibilityPromptRunMeta | null;
  /** Route where admins edit these prompts (e.g. '/crm/prompts'). */
  editable_in: string;
}
